import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSecondaryNavActions } from "@client/components/secondary-nav";
import { DataTable } from "@tubo/ui/components/ui/data-table";
import type { DataTableColumn } from "@tubo/ui/components/ui/data-table";
import { ListPageHeaderActions, ListSearchInput } from "@client/components/common/list-page-controls";
import { Button } from "@tubo/ui/components/ui/button";
import { Input } from "@tubo/ui/components/ui/input";
import { Badge } from "@tubo/ui/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@tubo/ui/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tubo/ui/components/ui/select";
import { Plus, Database } from "lucide-react";

export const Route = createFileRoute("/_app/data-center/datasources")({
  component: DataSourcesPage,
});

const DB_TYPES = [
  "MySQL",
  "PostgreSQL",
  "SQLServer",
  "Oracle",
  "DM",
  "KingBase",
  "OceanBase",
  "TiDB",
  "MariaDB",
];

type DatasourceStatus = "connected" | "disconnected" | "error";

interface DatasourceItem {
  id: string;
  name: string;
  type: string;
  host: string;
  database: string;
  status: DatasourceStatus;
  tableCount: number;
}

function DataSourcesPage() {
  const { t } = useTranslation("dataCenter");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const statusLabels: Record<DatasourceStatus, string> = {
    connected: t("dataSources.connected"),
    disconnected: t("dataSources.disconnected"),
    error: t("dataSources.error"),
  };
  const columns: DataTableColumn<DatasourceItem>[] = [
    { id: "name", header: t("dataSources.name"), cell: (row) => <span className="font-medium">{row.name}</span> },
    { id: "type", header: t("dataSources.type"), className: "w-32", cell: (row) => row.type },
    { id: "host", header: t("dataSources.host"), className: "w-40", cell: (row) => row.host },
    { id: "database", header: t("dataSources.database"), className: "w-40", cell: (row) => row.database },
    { id: "status", header: t("dataSources.status"), align: "center", className: "w-24", cell: (row) => <Badge variant={row.status === "connected" ? "default" : row.status === "error" ? "destructive" : "secondary"}>{statusLabels[row.status]}</Badge> },
    { id: "tableCount", header: t("dataSources.tableCount"), align: "center", className: "w-24", cell: (row) => row.tableCount },
    { id: "actions", header: t("dataSources.actions"), align: "center", className: "w-24", cell: () => null },
  ];

  useSecondaryNavActions(
    <ListPageHeaderActions>
      <ListSearchInput
        placeholder={t("dataSources.search")}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
        }}
      />
      <Button size="sm" onClick={() => setShowCreate(true)}>
        <Plus className="mr-1.5 h-4 w-4" />
        {t("dataSources.add")}
      </Button>
    </ListPageHeaderActions>,
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <DataTable
        columns={columns}
        data={[]}
        rowKey={(row) => row.id}
        page={1}
        pageSize={20}
        total={0}
        onPageChange={() => undefined}
        emptyState={
          <div className="flex h-64 flex-col items-center justify-center text-muted-foreground">
            <Database className="mb-3 h-12 w-12 text-icon-secondary-muted" />
            <p className="text-sm font-medium">{t("dataSources.empty")}</p>
            <p className="text-xs text-muted-foreground/70">
              {t("dataSources.emptyDescription")}
            </p>
          </div>
        }
      />

      <CreateDataSourceDialog open={showCreate} onOpenChange={setShowCreate} />
    </div>
  );
}

function CreateDataSourceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation("dataCenter");
  const [form, setForm] = useState({
    name: "",
    type: "MySQL",
    host: "",
    port: 3306,
    dbName: "",
    username: "",
    password: "",
  });

  const handleSubmit = async () => {
    // TODO: 调用 API 创建数据源
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("dataSources.dialogTitle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("dataSources.sourceName")}</label>
            <Input
              placeholder={t("dataSources.sourceNamePlaceholder")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("dataSources.databaseType")}</label>
              <Select
                value={form.type}
                onValueChange={(v) => v && setForm({ ...form, type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DB_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("dataSources.port")}</label>
              <Input
                type="number"
                value={form.port}
                onChange={(e) =>
                  setForm({ ...form, port: Number(e.target.value) })
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("dataSources.host")}</label>
            <Input
              placeholder="localhost"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t("dataSources.databaseName")}</label>
            <Input
              placeholder="mydb"
              value={form.dbName}
              onChange={(e) => setForm({ ...form, dbName: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("dataSources.username")}</label>
              <Input
                placeholder="root"
                value={form.username}
                onChange={(e) =>
                  setForm({ ...form, username: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("dataSources.password")}</label>
              <Input
                type="password"
                placeholder="••••••"
                value={form.password}
                onChange={(e) =>
                  setForm({ ...form, password: e.target.value })
                }
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("dataSources.cancel")}
          </Button>
          <Button onClick={handleSubmit}>{t("dataSources.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
